import { Box, Skeleton, Tabs, Tab, Typography } from '@mui/material';
import PrimaryButton from '../Common/PrimaryButton';
import IcRequired from '../../Icons/ic_leak_required.svg';
import IcCompleted from '../../Icons/ic_leak_completed.svg';
import IcMissed from '../../Icons/ic_leak_missed.svg';

const LeakInspectionSubCard = ({
    frequencies,
    loading,
    activeFrequency,
    onTabChange,
    showTable,
    onViewDetails,
    onHideDetails,
}) => {
    if (loading) {
        return (
            <Box sx={{ width: '100%', minHeight: 272, borderRadius: '10px', border: '1px solid #EBEDF8', bgcolor: '#fff', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', gap: 2, px: 2, pt: 1, borderBottom: '1px solid #EBEDF8', pb: 1 }}>
                    <Skeleton variant="text" width={80} height={36} />
                    <Skeleton variant="text" width={80} height={36} />
                    <Skeleton variant="text" width={60} height={36} />
                </Box>
                <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Skeleton variant="text" width={80} height={44} />
                            <Skeleton variant="text" width={120} height={18} />
                        </Box>
                        <Box sx={{ display: 'flex', border: '1px solid #EBEDF8', borderRadius: '10px', overflow: 'hidden' }}>
                            {[0, 1, 2].map(i => (
                                <Box key={i} sx={{ px: 2, py: 1.5, borderRight: i < 2 ? '1px solid #EBEDF8' : 'none', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Skeleton variant="circular" width={36} height={36} />
                                    <Box>
                                        <Skeleton variant="text" width={40} height={20} />
                                        <Skeleton variant="text" width={60} height={14} />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: '4px' }} />
                </Box>
            </Box>
        );
    }

    const activeData = frequencies.find(f => f.name === activeFrequency) || {};
    const { required, completed, missed, complianceRate } = activeData;

    const handleToggle = () => {
        if (showTable) {
            onHideDetails();
        } else {
            onViewDetails(activeFrequency);
        }
    };

    return (
        <Box sx={{ borderRadius: '10px', overflow: 'hidden', bgcolor: '#fff', border: '1px solid #EBEDF8', minHeight: 272, width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
            <Tabs
                value={activeFrequency}
                onChange={(_, val) => onTabChange(val)}
                sx={{
                    borderBottom: '1px solid #EBEDF8',
                    minHeight: 44,
                    '& .MuiTabs-indicator': { backgroundColor: '#008299', height: 3 },
                    '& .MuiTab-root': {
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: '#888',
                        minHeight: 44,
                        '&.Mui-selected': { color: '#008299' },
                    },
                }}
            >
                {frequencies.map(f => (
                    <Tab key={f.name} label={f.name} value={f.name} />
                ))}
            </Tabs>

            {/* Body */}
            <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>

                {/* Stats row — vertically centered */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>

                        {/* Compliance rate */}
                        <Box sx={{ minWidth: 90 }}>
                            <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: '#1A2332', lineHeight: 1 }}>
                                {complianceRate != null ? `${complianceRate}%` : '-'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 500, mt: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Compliance Rate
                            </Typography>
                        </Box>

                        {/* Stat items */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', border: '1px solid #EBEDF8', borderRadius: '10px', overflow: 'hidden' }}>
                            {[
                                { icon: IcRequired,  value: required,  label: 'Required' },
                                { icon: IcCompleted, value: completed, label: 'Completed' },
                                { icon: IcMissed,    value: missed,    label: 'Missed' },
                            ].map(({ icon, value: v, label }) => (
                                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
                                    <img src={icon} alt={label} width={36} height={36} style={{ flexShrink: 0 }} />
                                    <Box>
                                        <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#1A2332', lineHeight: 1 }}>
                                            {v ?? '-'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {label}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <PrimaryButton
                        buttonText={showTable ? 'Hide Details' : 'View Details'}
                        onClick={handleToggle}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default LeakInspectionSubCard;
